import { useReducer, useCallback } from "react";
import type { Updater } from "src/utils/immutable-helpers";
import { produce } from "immer";

enum ActionType {
  Undo = "UNDO",
  Redo = "REDO",
  Set = "SET",
  Update = "UPDATE",
  Reset = "RESET",
}

export interface Actions<T> {
  /** Sets the state. Prefer `update` */
  set: (newPresent: T, checkpoint?: boolean) => void;

  /** Update state via a function that has access to the previous state value. Similar to the functional setter from `useState`.*/
  update: (
    /** Update state by "mutating" the provided draft. It will be converted to an immutable update. Or return a completely new state. Powered by `immer`. */
    updater: Updater<T>,
    options?: { checkpoint?: boolean },
  ) => void;
  reset: (newPresent: T) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

type UndoAction = { type: ActionType.Undo };
type RedoAction = { type: ActionType.Redo };
type ResetAction<T> = { type: ActionType.Reset; newPresent: T };
type SetAction<T> = {
  type: ActionType.Set;
  newPresent: T;
  historyCheckpoint?: boolean;
};
type UpdateAction<T> = {
  type: ActionType.Update;
  updater: Updater<T>;
  historyCheckpoint?: boolean;
};

type Action<T> =
  | UndoAction
  | RedoAction
  | ResetAction<T>
  | SetAction<T>
  | UpdateAction<T>;

export interface State<T> {
  past: T[];
  present: T;
  future: T[];
}

const initialState = {
  past: [],
  present: null,
  future: [],
};

type Options = {
  useCheckpoints?: boolean;
};

export const useUndo = <T>(
  initialPresent: T,
  opts: Options = {},
): [State<T>, Actions<T>] => {
  const { useCheckpoints }: Options = {
    useCheckpoints: false,
    ...opts,
  };

  const reducer = (state: State<T>, action: Action<T>): State<T> => {
    const { past, present, future } = state;

    switch (action.type) {
      case ActionType.Undo: {
        const newPast = [...past];
        const previous = newPast.pop();

        if (previous === undefined) {
          return state;
        }

        return {
          past: newPast,
          present: previous,
          future: [present, ...future],
        };
      }

      case ActionType.Redo: {
        if (future.length === 0) {
          return state;
        }
        const next = future[0];
        const newFuture = future.slice(1);

        return {
          past: [...past, present],
          // Guaranteed by length check
          present: next as T,
          future: newFuture,
        };
      }

      case ActionType.Set: {
        const { newPresent } = action;

        if (newPresent === present) {
          return state;
        }

        const isNewCheckpoint = useCheckpoints
          ? !!action.historyCheckpoint
          : true;

        return {
          past: isNewCheckpoint === false ? past : [...past, present],
          present: newPresent,
          future: [],
        };
      }

      case ActionType.Update: {
        const { updater } = action;

        const newPresent = produce(present, updater);

        if (newPresent === present) {
          return state;
        }

        const isNewCheckpoint = useCheckpoints
          ? !!action.historyCheckpoint
          : true;

        return {
          past: isNewCheckpoint === false ? past : [...past, present],
          present: newPresent,
          future: [],
        };
      }

      case ActionType.Reset: {
        const { newPresent } = action;

        return {
          past: [],
          present: newPresent,
          future: [],
        };
      }
    }
  };

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    present: initialPresent,
  });

  const canUndo = state.past.length !== 0;
  const canRedo = state.future.length !== 0;

  const undo = useCallback(() => {
    if (canUndo) {
      dispatch({ type: ActionType.Undo });
    }
  }, [canUndo]);

  const redo = useCallback(() => {
    if (canRedo) {
      dispatch({ type: ActionType.Redo });
    }
  }, [canRedo]);

  const set = useCallback((newPresent: T, checkpoint = true) => {
    dispatch({
      type: ActionType.Set,
      newPresent,
      historyCheckpoint: checkpoint,
    });
  }, []);

  const update = useCallback(
    (
      updater: Updater<T>,
      { checkpoint = true }: { checkpoint?: boolean } = {},
    ) => {
      dispatch({
        type: ActionType.Update,
        updater,
        historyCheckpoint: checkpoint,
      });
    },
    [],
  );

  const reset = useCallback(
    (newPresent: T) => dispatch({ type: ActionType.Reset, newPresent }),
    [],
  );

  return [
    state as State<T>,
    { set, update, reset, undo, redo, canUndo, canRedo },
  ];
};
